import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { defineSteps, step } from 'flows/common/helpers/defineSteps'
import { BrandPlanAwarenessRedesign } from 'components/FirstTimeExperienceModal/steps/BrandPlanAwareness/BrandPlanAwarenessRedesign'
import { BrandPlanTactics } from 'components/FirstTimeExperienceModal/steps/BrandPlanTactics/BrandPlanTactics'
import { BrandPlanTacticsRedesign } from 'components/FirstTimeExperienceModal/steps/BrandPlanTactics/BrandPlanTacticsRedesign'
import { BrandProfileStep } from 'components/FirstTimeExperienceModal/steps/BrandProfile/BrandProfileStep'
import { BrandProfileWebsite } from 'screens/VirtualMarketerOnboarding/BrandProfileWebsite/BrandProfileWebsite'

import { FlowCtx } from 'flows/common/machines/createFlowMachine'
import { StepProps } from 'flows/virtualMarketerOnboarding/Types'
import { CampaignDetails } from 'components/FirstTimeExperienceModal/steps/CampaignGeneration/CampaignDetails'
import {
  CampaignGenerationLoadingScreen,
  CampaignGenerationLoadingProvider,
} from 'components/FirstTimeExperienceModal/steps/CampaignGeneration'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { useDeleteBrandKitSource } from 'hooks/queries/useDeleteBrandKitSource'
import { useDeleteBrandProfile } from 'hooks/queries/brandProfiles/useDeleteBrandProfile'
import { AutopilotSidebar as ProfileAutopilotSidebar } from 'components/FirstTimeExperienceModal/steps/BrandProfile/AutopilotSidebar'
import { ScreenshotFallback } from 'screens/VirtualMarketerOnboarding/ScreenshotFallback/ScreenshotFallback'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { NoWebsite } from 'screens/VirtualMarketerOnboarding/NoWebsite/NoWebsite'
import { BrandVisualStyleSelection } from 'screens/VirtualMarketerOnboarding/BrandVisualStyleSelection'
import { useDisplayedFontPairingsAndVisualStyles } from 'screens/VirtualMarketerOnboarding/BrandVisualStyleSelection/useDisplayedFontPairingsAndVisualStyles'
import { useVisualStyles } from 'hooks/queries/visualStyles'
import { BrandVisualStylePreview } from 'screens/VirtualMarketerOnboarding/BrandVisualStylePreview'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { BrandVisualStyleFonts } from 'screens/VirtualMarketerOnboarding/BrandVisualStyleFonts/BrandVisualStyleFonts'
import { BrandVisualStyleVoiceAndLogo } from 'screens/VirtualMarketerOnboarding/BrandVisualStyleVoiceAndLogo/BrandVisualStyleVoiceAndLogo'
import { useIsPioneerStarterOrGrowth } from 'hooks/useIsPioneerStarterOrGrowth'
import { useChooseOnboardingPlan } from './useChooseOnboardingPlan'
import { useExperiment } from 'utils/experiments'
import { StrategiesSelection } from 'screens/VirtualMarketerOnboarding/StrategiesSelection/StrategiesSelection'
import { Strategy } from 'screens/VirtualMarketerOnboarding/StrategiesSelection/utils'
import { GoalSelection } from 'screens/VirtualMarketerOnboarding/GoalSelection/GoalSelection'
import { BrandProfileForStrategiesStep } from 'components/FirstTimeExperienceModal/steps/BrandProfile/BrandProfileForStrategiesStep'
import { NextGenContentPreferences } from 'components/FirstTimeExperienceModal/steps/NextGenContentPreferences/NextGenContentPreferences'
import { V4ContentPreferences } from 'screens/VirtualMarketerOnboarding/V4VisualStyleSelection/V4ContentPreferences'
import { MobileGenerationAndQuestionnaire } from 'screens/VirtualMarketerOnboarding/MobileGenerationAndQuestionnaire/MobileGenerationAndQuestionnaire'
import { getLocation } from 'hooks/useHistory'
import { BusinessType } from 'flows/common/steps/BusinessType/BusinessType'
import { useOnboardingTracking } from 'hooks/useOnboardingTracking'
import { V4CreateAndPrepare } from 'screens/VirtualMarketerOnboarding/V4CreateAndPrepare/V4CreateAndPrepare'
import { V4EditCampaign } from 'screens/VirtualMarketerOnboarding/V4EditCampaign/V4EditCampaign'
import { V4ChannelSelection } from 'screens/VirtualMarketerOnboarding/V4ChannelSelection/V4ChannelSelection'
import { V4ContentRecommendations } from 'screens/VirtualMarketerOnboarding/V4ContentRecommendations/V4ContentRecommendations'
import { V4PrepareTopics } from 'screens/VirtualMarketerOnboarding/V4PrepareTopics/V4PrepareTopics'
import { V4ReviewTopics } from 'screens/VirtualMarketerOnboarding/V4ReviewTopics/V4ReviewTopics'
import { V4VisualStyleSelection } from 'screens/VirtualMarketerOnboarding/V4VisualStyleSelection/V4VisualStyleSelection'
import { V4CampaignPreview } from 'screens/VirtualMarketerOnboarding/V4CampaignPreview/V4CampaignPreview'
import { V4WebsiteEntry } from 'screens/VirtualMarketerOnboarding/V4WebsiteEntry/V4WebsiteEntry'
import { V4ProfileLoader } from 'screens/VirtualMarketerOnboarding/V4ProfileLoader/V4ProfileLoader'
import { V4StrategySelection } from 'screens/VirtualMarketerOnboarding/V4StrategySelection/V4StrategySelection'
import { V4BrandMaterials } from 'screens/VirtualMarketerOnboarding/V4BrandMaterials/V4BrandMaterials'
import { ContentOptimization } from 'screens/VirtualMarketerOnboarding/ContentOptimization/ContentOptimization'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { useInfiniteBrandKitMediaFiles } from 'hooks/queries/brandKits/useInfiniteBrandKitMediaFiles'
import { useBrandKitSources } from 'hooks/queries/useBrandKitSources'
import { useBrandPlanSchedules } from 'hooks/queries/schedules/useBrandPlanSchedules'
import { fetchBrandPlanSchedule } from 'hooks/queries/schedules/useBrandPlanSchedule'
import { startBrandPlan } from 'hooks/queries/brandPlans/useStartBrandPlan'
import { generateCampaignContent } from 'hooks/queries/schedules/useGenerateCampaignContent'
import { prepareTopics } from 'hooks/queries/schedules/usePrepareTopics'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useOnboard } from 'hooks/queries/useOnboard'
import { useCompleteVMOnboarding } from 'hooks/queries/workspaces/useCompleteVMOnboarding'
import { reportError } from 'utils/error'
import { useDeleteSchedule } from 'hooks/queries/schedules/useDeleteSchedule'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import dayjs from 'dayjs'

type OnboardingFlow = 'legacy' | 'strategies' | 'v4' | 'v4slim'

let flowOverrideTracked = false

export const useOnboardingFlow = (): OnboardingFlow => {
  const { isMobile } = useResponsiveDesign()
  const flowParam = new URLSearchParams(getLocation().search).get('flow')

  const strategiesVariant = useExperiment('onboarding_strategies')
  const strategiesMobileVariant = useExperiment('onboarding_strategies_mobile')
  const v4Variant = useExperiment('onboarding_v4')
  const v4MobileVariant = useExperiment('onboarding_v4_mobile')

  let flow: OnboardingFlow

  if (flowParam === 'legacy') {
    flow = 'legacy'
  } else if (flowParam === 'strategiesold') {
    flow = 'strategies'
  } else if (flowParam === 'strategiesnewslim') {
    flow = 'v4slim'
  } else if (flowParam === 'strategiesnew') {
    flow = 'v4'
  } else if (flowParam === 'strategies') {
    const isV4On = isMobile ? v4MobileVariant === 'on' : v4Variant === 'on'
    flow = isV4On ? 'v4' : 'strategies'
  } else {
    const isStrategiesOn = isMobile ? strategiesMobileVariant === 'on' : strategiesVariant === 'on'
    if (!isStrategiesOn) {
      flow = 'legacy'
    } else {
      const isV4On = isMobile ? v4MobileVariant === 'on' : v4Variant === 'on'
      flow = isV4On ? 'v4' : 'strategies'
    }
  }

  const { trackOnboardingFlowStarted } = useOnboardingTracking()

  useEffect(() => {
    if (!flowParam || flowOverrideTracked) return
    flowOverrideTracked = true
    trackOnboardingFlowStarted(flow, 'url_override', flowParam)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return flow
}

export const useOnboardingStrategiesEnabled = () => useOnboardingFlow() !== 'legacy'

export const useOnboardingV4Enabled = () => {
  const flow = useOnboardingFlow()
  return flow === 'v4' || flow === 'v4slim'
}

export const useOnboardingV4SlimEnabled = () => useOnboardingFlow() === 'v4slim'

const useCleanupBrandProfile = (brandProfileId: number, brandKitId?: number) => {
  const { data: brandKitData } = useBrandKit(brandKitId, { cacheTime: 1000 * 60 * 5 })
  const { mutate: deleteBrandProfile } = useDeleteBrandProfile({ id: brandProfileId })
  const { mutate: deleteBrandKitSource } = useDeleteBrandKitSource()

  const cleanupBrandProfile = useCallback(() => {
    deleteBrandProfile()
    const brandKitSources = brandKitData?.brand_kit?.brand_kit_sources
    brandKitSources?.forEach((source) => {
      brandKitId && deleteBrandKitSource({ brandKitId, id: source.id })
    })
  }, [deleteBrandProfile, brandKitData?.brand_kit?.brand_kit_sources, brandKitId, deleteBrandKitSource])

  return { cleanupBrandProfile }
}

const BrandProfileWebsiteWrapper = ({ send, context }: any) => {
  let effectiveBrandKitId = context.brandKitId
  if (!context.isIntentionalBrandKit) {
    effectiveBrandKitId = null
  }

  return (
    <BrandProfileWebsite
      websites={context.websites}
      language={context.language || context.defaultLanguage}
      brandKitId={effectiveBrandKitId}
      onBack={() => send({ type: 'BACK' })}
      onNext={async (brandProfile, brandVisualEnhancementId) => {
        let analysisId = brandProfile.latest_analysis_id

        send({
          type: 'NEXT',
          payload: {
            brandProfileId: brandProfile.id,
            brandKitId: brandProfile.brand_kit.id,
            websiteUrl: brandProfile.url,
            brandPlanId: brandProfile.brand_plan.id,
            analysisId,
            brandVisualEnhancementId,
            websiteScrapeSucceeded: true,
          },
        })
      }}
      onNoWebsite={() => {
        send({ type: 'NO_WEBSITE' })
      }}
    />
  )
}

const V4WebsiteEntryWrapper = ({ send, context }: StepProps) => {
  const effectiveBrandKitId = context.isIntentionalBrandKit ? context.brandKitId : null

  return (
    <V4WebsiteEntry
      language={context.language || context.defaultLanguage}
      brandKitId={effectiveBrandKitId}
      onNext={(brandProfile, brandVisualEnhancementId) => {
        send({
          type: 'NEXT',
          payload: {
            brandProfileId: brandProfile.id,
            brandKitId: brandProfile.brand_kit.id,
            websiteUrl: brandProfile.url,
            brandPlanId: brandProfile.brand_plan.id,
            analysisId: brandProfile.latest_analysis_id,
            brandVisualEnhancementId,
            websiteScrapeSucceeded: true,
          },
        })
      }}
    />
  )
}

const V4ProfileLoaderWrapper = ({ send, context }: StepProps) => {
  const { cleanupBrandProfile } = useCleanupBrandProfile(context.brandProfileId, context.brandKitId)

  const handleBack = useCallback(
    (withError = false) => {
      cleanupBrandProfile()

      send({
        type: 'MERGE_CTX',
        payload: {
          brandProfileId: null,
          brandProfile: null,
          skipUploadBrandMaterials: false,
          brandKitId: context.isIntentionalBrandKit ? context.brandKitId : null,
          isIntentionalBrandKit: context.isIntentionalBrandKit || false,
          profile: null,
          websiteScrapeSucceeded: !withError,
        },
      })

      send({ type: 'BACK' })
    },
    [cleanupBrandProfile, context.brandKitId, context.isIntentionalBrandKit, send],
  )

  if (!context.analysisId || !context.brandProfileId) return null

  return (
    <V4ProfileLoader
      analysisId={context.analysisId as number}
      brandProfileId={context.brandProfileId as number}
      brandKitId={context.brandKitId as number | undefined}
      websiteUrl={context.websiteUrl}
      onReady={() => send({ type: 'NEXT' })}
      onBack={handleBack}
    />
  )
}

const MarketingQuestionsWrapper = ({ send }: StepProps) => {
  const { trackOnboardingEvent } = useOnboardingTracking()

  return (
    <BusinessType
      onNext={() => {
        trackOnboardingEvent('Event::MarketingQuestionsCompletedInOnboarding', {
          source: 'vm_onboarding_gate',
        })
        send({ type: 'NEXT' })
      }}
    />
  )
}

const ScreenshotFallbackWrapper = ({ send, context }: StepProps) => {
  return (
    <ScreenshotFallback
      onBack={() => send({ type: 'BACK' })}
      brandKitId={context.brandKitId}
      url={context.websiteUrl}
      selectedLanguage={context.selectedLanguage}
      onNext={async (brandProfile) => {
        send({
          type: 'NEXT',
          payload: {
            brandProfileId: brandProfile.id,
            brandKitId: brandProfile.brand_kit.id,
            websiteUrl: brandProfile.url,
            brandPlanId: brandProfile.brand_plan.id,
            analysisId: brandProfile.latest_analysis_id,
            websiteScrapeSucceeded: true,
          },
        })
      }}
    />
  )
}

const NoWebsiteWrapper = ({ send, context }: StepProps) => {
  return (
    <NoWebsite
      onBack={() => send({ type: 'BACK' })}
      brandKitId={context.brandKitId}
      url={context.websiteUrl}
      selectedLanguage={context.selectedLanguage}
      onNext={async (brandProfile) => {
        send({
          type: 'NEXT',
          payload: {
            brandProfileId: brandProfile.id,
            brandKitId: brandProfile.brand_kit.id,
            websiteUrl: brandProfile.url,
            brandPlanId: brandProfile.brand_plan.id,
            analysisId: brandProfile.latest_analysis_id,
          },
        })
      }}
    />
  )
}

const BrandProfileStepWrapper = ({ send, context }: StepProps) => {
  const { cleanupBrandProfile } = useCleanupBrandProfile(context.brandProfileId, context.brandKitId)
  const { isMobile } = useResponsiveDesign()

  const handleBack = useCallback(
    (websiteScrapeSucceeded = true) => {
      cleanupBrandProfile()

      send({
        type: 'MERGE_CTX',
        payload: {
          brandProfileId: null,
          brandProfile: null,
          skipUploadBrandMaterials: false,
          brandKitId: context.isIntentionalBrandKit ? context.brandKitId : null,
          isIntentionalBrandKit: context.isIntentionalBrandKit || false,
          profile: null,
          websiteScrapeSucceeded,
        },
      })

      send({ type: 'BACK' })
    },
    [cleanupBrandProfile, context.brandKitId, context.isIntentionalBrandKit, send],
  )

  const { saveSubscriptionPlan } = useChooseOnboardingPlan()

  return (
    <BrandProfileStep
      analysisId={context.analysisId}
      id={context.brandProfileId}
      initialProfile={context.profile}
      deleteOnBack={false}
      onAnalysis={(analysisId) => {
        send({ type: 'MERGE_CTX', payload: { analysisId } })
      }}
      onNext={async (profile) => {
        const subscriptionPriceId = await saveSubscriptionPlan()

        send({
          type: 'MERGE_CTX',
          payload: { profile, ...(subscriptionPriceId && { subscriptionPriceId }) },
        })

        if (isMobile) {
          send({ type: 'NEXT_MOBILE' })
        } else {
          send({ type: 'NEXT' })
        }
      }}
      onBack={handleBack}
    />
  )
}

const BrandProfileForStrategiesStepWrapper = ({ send, context }: StepProps) => {
  const { cleanupBrandProfile } = useCleanupBrandProfile(context.brandProfileId, context.brandKitId)
  const { mutateAsync: deleteSchedule } = useDeleteSchedule()
  const [isDeletingSchedule, setIsDeletingSchedule] = useState(false)

  const handleBack = useCallback(
    (websiteScrapeSucceeded = true) => {
      cleanupBrandProfile()

      send({
        type: 'MERGE_CTX',
        payload: {
          brandProfileId: null,
          brandProfile: null,
          skipUploadBrandMaterials: false,
          brandKitId: context.isIntentionalBrandKit ? context.brandKitId : null,
          isIntentionalBrandKit: context.isIntentionalBrandKit || false,
          profile: null,
          websiteScrapeSucceeded,
        },
      })

      send({ type: 'BACK' })
    },
    [cleanupBrandProfile, context.brandKitId, context.isIntentionalBrandKit, send],
  )

  return (
    <BrandProfileForStrategiesStep
      analysisId={context.analysisId}
      id={context.brandProfileId}
      initialProfile={context.profile}
      language={context.language || context.defaultLanguage}
      onLanguageChange={(language) => {
        send({ type: 'MERGE_CTX', payload: { language } })
      }}
      onAnalysis={(analysisId) => {
        send({ type: 'MERGE_CTX', payload: { analysisId } })
      }}
      onNext={async (profile, selectedBusinessType) => {
        const businessTypeChanged =
          context.selectedBusinessType != null && context.selectedBusinessType !== selectedBusinessType

        const payload: Record<string, unknown> = { profile, selectedBusinessType }

        if (businessTypeChanged && context.v4ScheduleId) {
          setIsDeletingSchedule(true)
          try {
            await deleteSchedule({ id: context.v4ScheduleId })
          } catch (e) {
            reportError(e)
          }
          setIsDeletingSchedule(false)
          payload.v4ScheduleId = null
          payload.brandPlanStrategies = null
        }

        send({ type: 'MERGE_CTX', payload })
        send({ type: 'NEXT' })
      }}
      isSaving={isDeletingSchedule}
      onBack={handleBack}
    />
  )
}

const BrandPlanAwarenessWrapper = ({ send, context, onProvideBackHandler }: StepProps) => {
  const planVariant = useIsPioneerStarterOrGrowth()

  if (!context.brandPlanId) {
    throw new Error('Brand plan ID is required for BrandPlanAwarenessStep')
  }

  const isStarterPlan = planVariant === 'starter'

  const handleNext = (payload?: any) => {
    if (payload?.desktopPath && payload?.publishDay) {
      send({ type: 'MOBILE_COMPLETE', payload })
    } else {
      send({ type: 'NEXT', payload })
    }
  }

  return (
    <BrandPlanAwarenessRedesign
      id={context.brandPlanId}
      selectedBlogPlatform={context.selectedBlogPlatform}
      selectedEmailPlatform={context.selectedEmailPlatform}
      isStarterPlan={isStarterPlan}
      subscriptionPriceId={context.subscriptionPriceId}
      paidAdsOnboardingParameters={context.paidAdsOnboardingParameters}
      onBack={() => send({ type: 'BACK' })}
      onProvideBackHandler={onProvideBackHandler}
      onNext={handleNext}
    />
  )
}

const WhereToPublishWrapper = ({ send, context, onProvideBackHandler }: StepProps) => {
  const planVariant = useIsPioneerStarterOrGrowth()
  const isOnboardingAddVoicePageFlag = useFlipperFlag('onboarding_add_voice_page')

  if (!context.brandPlanId) {
    throw new Error('Brand plan ID is required for WhereToPublish')
  }

  const mode = planVariant === 'starter' ? 'starter' : 'campaign'

  const handleBack = () => {
    if (isOnboardingAddVoicePageFlag) {
      send({ type: 'BACK_TO_VISUAL_STYLES_VOICE' })
    } else {
      send({ type: 'BACK_TO_FONTS' })
    }
  }

  const handleNext = ({ tactics, selectedBlogPlatform, selectedEmailPlatform, paidAdsOnboardingParameters }: any) => {
    const payload = { tactics, selectedBlogPlatform, selectedEmailPlatform, paidAdsOnboardingParameters }

    if (planVariant === 'starter') {
      send({ type: 'NEXT_STARTER', payload })
    } else {
      send({ type: 'NEXT_GROWTH', payload })
    }
  }

  // Starter flow uses the redesigned tactics page
  if (mode === 'starter') {
    return (
      <BrandPlanTacticsRedesign
        id={context.brandPlanId}
        mode={mode}
        tactics={context.tactics || []}
        seoKeywords={context.seoKeywords || []}
        idealCustomers={context.idealCustomers || []}
        onBack={handleBack}
        onProvideBackHandler={onProvideBackHandler}
        onNext={handleNext}
      />
    )
  }

  // Growth/campaigns flow uses the original tactics component
  return (
    <BrandPlanTactics
      id={context.brandPlanId}
      mode={mode}
      externalIntegrations={context.externalIntegrations}
      brandKitId={context.brandKitId}
      tactics={context.tactics}
      seoKeywords={context.seoKeywords}
      idealCustomers={context.idealCustomers}
      initialInsightChannels={context.insightChannels || []}
      onBack={handleBack}
      onProvideBackHandler={onProvideBackHandler}
      onNext={handleNext}
    />
  )
}

const CampaignGenerationLoadingWrapper = ({ send, context }: StepProps) => {
  if (!context.brandPlanId) {
    throw new Error('Brand plan ID is required for CampaignGenerationLoading')
  }

  return (
    <CampaignGenerationLoadingProvider>
      <CampaignGenerationLoadingScreen
        brandPlanId={context.brandPlanId}
        brandProfileId={context.brandProfileId}
        analysisId={context.analysisId}
        campaignData={context.campaignData}
        startDate={context.startDate}
        subscriptionPriceId={context.subscriptionPriceId}
        onComplete={() => {
          send({ type: 'COMPLETE' })
        }}
        onBack={() => send({ type: 'BACK' })}
      />
    </CampaignGenerationLoadingProvider>
  )
}

const MobileGenerationAndQuestionnaireWrapper = ({ send, context }: StepProps) => {
  if (!context.brandPlanId || !context.brandProfileId || !context.analysisId) {
    throw new Error(
      'Brand plan ID, brand profile ID, and analysis ID are required for MobileGenerationAndQuestionnaire',
    )
  }

  return <MobileGenerationAndQuestionnaire scheduleId={context.v4ScheduleId ?? null} />
}

const CampaignDetailsWrapper = ({ send, context, onProvideBackHandler }: StepProps) => {
  if (!context.brandPlanId || !context.brandKitId) {
    throw new Error('Brand plan ID and brand kit ID are required for CampaignDetails')
  }

  return (
    <CampaignDetails
      brandPlanId={context.brandPlanId}
      brandKitId={context.brandKitId}
      initialValues={context.campaignData}
      paidAdsOnboardingParameters={context.paidAdsOnboardingParameters}
      onBack={() => send({ type: 'BACK' })}
      onProvideBackHandler={onProvideBackHandler}
      onNext={(campaignData) => {
        send({
          type: 'NEXT',
          payload: {
            campaignData: {
              growth_goals_input: campaignData.growth_goals_input,
              key_dates_input: campaignData.key_dates_input,
            },
            startDate: campaignData.startDate,
          },
        })
      }}
    />
  )
}

const BrandVisualStyleSelectionWrapper = ({ send, context }: StepProps) => {
  return (
    <BrandVisualStyleSelection
      brandKitId={context.brandKitId}
      onBack={() => {
        send({ type: 'BACK' })
      }}
      onNext={(fontPairings, stylesGenerated, sourceImage) => {
        send({
          type: 'MERGE_CTX',
          payload: { fontPairings, stylesGenerated, sourceImage },
        })
        send({ type: 'NEXT' })
      }}
    />
  )
}

const GoalSelectionWrapper = ({ send, context }: StepProps) => {
  if (!context.brandPlanId) {
    throw new Error('Brand plan ID is required for GoalSelection')
  }

  return (
    <GoalSelection
      selectedBusinessType={context.selectedBusinessType}
      initialSelectedGoal={context.goal}
      onBack={() => {
        send({ type: 'BACK' })
      }}
      onNext={(goal, brandPlanStrategies) => {
        send({
          type: 'MERGE_CTX',
          payload: { goal, brandPlanStrategies },
        })
        send({ type: 'NEXT' })
      }}
    />
  )
}

const StrategiesSelectionWrapper = ({ send, context }: StepProps) => {
  const [isSavingSubscriptionPlan, setIsSavingSubscriptionPlan] = useState(false)
  const { saveSubscriptionPlan } = useChooseOnboardingPlan()

  if (!context.brandPlanId) {
    throw new Error('Brand plan ID is required for StrategiesSelection')
  }

  return (
    <StrategiesSelection
      brandPlanId={context.brandPlanId}
      initialSelectedStrategies={context.brandPlanStrategies}
      selectedBusinessType={context.selectedBusinessType}
      goal={context.goal}
      isSaving={isSavingSubscriptionPlan}
      onNext={async (brandPlanStrategies, planKey) => {
        setIsSavingSubscriptionPlan(true)
        const subscriptionPriceId = await saveSubscriptionPlan(planKey)
        setIsSavingSubscriptionPlan(false)
        send({
          type: 'MERGE_CTX',
          payload: { brandPlanStrategies, ...(subscriptionPriceId && { subscriptionPriceId }) },
        })
        send({ type: 'NEXT' })
      }}
      onBack={() => send({ type: 'BACK' })}
    />
  )
}

const BrandVisualStylePreviewWrapper = ({ send, context, onProvideBackHandler }: StepProps) => {
  const { isMobile } = useResponsiveDesign()

  const handleBack = () => {
    if (isMobile) {
      send({ type: 'BACK_MOBILE' })
    } else {
      send({ type: 'BACK' })
    }
  }

  return (
    <BrandVisualStylePreview
      onBack={handleBack}
      onProvideBackHandler={onProvideBackHandler}
      initialSelectedStyle={context.selectedVisualStyle}
      initialStylesGenerated={context.stylesGenerated}
      sourceImage={context.sourceImage ?? null}
      brandKitId={context.brandKitId}
      onNext={(selectedVisualStyle, stylesGenerated, fontPairings) => {
        send({
          type: 'MERGE_CTX',
          payload: {
            selectedVisualStyle,
            stylesGenerated,
            ...(fontPairings && { fontPairings }),
          },
        })
        send({ type: 'NEXT' })
      }}
    />
  )
}

const BrandVisualStyleFontsWrapper = ({ send, context, onProvideBackHandler }: StepProps) => {
  return (
    <BrandVisualStyleFonts
      onBack={() => send({ type: 'BACK' })}
      onProvideBackHandler={onProvideBackHandler}
      brandKitId={context.brandKitId}
      initialFontPairings={context.fontPairings}
      initialFontPairingId={context.selectedFontPairingId}
      selectedStyle={context.selectedVisualStyle}
      onNext={(selectedFontPairingId) => {
        send({
          type: 'MERGE_CTX',
          payload: { selectedFontPairingId },
        })
        send({ type: 'NEXT' })
      }}
    />
  )
}

const NextGenContentPreferencesWrapper = ({ send, context }: StepProps) => {
  const { isMobile } = useResponsiveDesign()

  if (!context.brandKitId || !context.brandPlanId) {
    throw new Error('Brand kit ID and brand plan ID are required for NextGenContentPreferences')
  }

  return (
    <NextGenContentPreferences
      onBack={() => send({ type: 'BACK' })}
      brandKitId={context.brandKitId}
      initialVariant={context.sourceImageEditLevel}
      brandPlanId={context.brandPlanId}
      campaignData={context.campaignData}
      startDate={context.startDate}
      subscriptionPriceId={context.subscriptionPriceId}
      onNext={(sourceImageEditLevel) => {
        send({
          type: 'MERGE_CTX',
          payload: { sourceImageEditLevel },
        })
        if (isMobile) {
          send({ type: 'NEXT_MOBILE' })
        } else {
          send({ type: 'NEXT' })
        }
      }}
    />
  )
}

const BrandVisualStyleVoiceAndLogoWrapper = ({ send, context }: StepProps) => {
  const isOnboardingAddVoicePageFlag = useFlipperFlag('onboarding_add_voice_page')

  if (!isOnboardingAddVoicePageFlag || !context.brandKitId) {
    send({ type: 'NEXT' })
    return null
  }

  return (
    <BrandVisualStyleVoiceAndLogo
      onNext={() => {
        send({ type: 'NEXT' })
      }}
      onBack={() => {
        send({ type: 'BACK' })
      }}
      brandKitId={context.brandKitId}
    />
  )
}

const V4StrategiesSelectionWrapper = ({ send, context }: StepProps) => {
  const [isSavingSubscriptionPlan, setIsSavingSubscriptionPlan] = useState(false)
  const [isDeletingSchedule, setIsDeletingSchedule] = useState(false)
  const { saveSubscriptionPlan } = useChooseOnboardingPlan()
  const { mutateAsync: deleteSchedule } = useDeleteSchedule()

  if (!context.brandPlanId) {
    throw new Error('Brand plan ID is required for StrategiesSelection')
  }

  return (
    <V4StrategySelection
      brandPlanId={context.brandPlanId}
      initialSelectedStrategies={context.brandPlanStrategies}
      selectedBusinessType={context.selectedBusinessType}
      goal={context.goal}
      isSaving={isSavingSubscriptionPlan || isDeletingSchedule}
      highlightStep="strategy"
      onNext={async (brandPlanStrategies, planKey) => {
        setIsSavingSubscriptionPlan(true)
        const subscriptionPriceId = await saveSubscriptionPlan(planKey)
        setIsSavingSubscriptionPlan(false)

        const prevSelectedId = context.brandPlanStrategies?.find((s: Strategy) => s.isSelected)?.id
        const nextSelectedId = brandPlanStrategies.find((s) => s.isSelected)?.id
        const strategyChanged = prevSelectedId != null && nextSelectedId != null && prevSelectedId !== nextSelectedId

        const payload: Record<string, unknown> = {
          brandPlanStrategies,
          ...(subscriptionPriceId && { subscriptionPriceId }),
        }

        if (strategyChanged && context.v4ScheduleId) {
          setIsDeletingSchedule(true)
          try {
            await deleteSchedule({ id: context.v4ScheduleId })
          } catch (e) {
            reportError(e)
          }
          setIsDeletingSchedule(false)
          payload.v4ScheduleId = null
        }

        send({ type: 'MERGE_CTX', payload })
        send({ type: 'NEXT_V4' })
      }}
      onBack={() => send({ type: 'BACK' })}
    />
  )
}

const V4CreateAndPrepareWrapper = ({ send, context }: StepProps) => {
  const { displayedVisualStyles, isLoading: isLoadingRecommendations } = useDisplayedFontPairingsAndVisualStyles(
    context.brandKitId,
  )
  const { data: visualStyles } = useVisualStyles()

  const recommendationsReady =
    !isLoadingRecommendations && (displayedVisualStyles.length > 0 || (visualStyles != null && visualStyles.length > 0))

  const pendingScheduleIdRef = useRef<number | null>(null)

  useEffect(() => {
    if (pendingScheduleIdRef.current != null && recommendationsReady) {
      const id = pendingScheduleIdRef.current
      pendingScheduleIdRef.current = null
      send({ type: 'MERGE_CTX', payload: { v4ScheduleId: id } })
      send({ type: 'NEXT' })
    }
  }, [recommendationsReady, send])

  if (!context.brandPlanId) {
    throw new Error('Brand plan ID is required for V4CreateAndPrepare')
  }

  const selectedStrategy = context.brandPlanStrategies?.find((s: Strategy) => s.isSelected)

  if (!selectedStrategy) {
    throw new Error('A selected strategy is required for V4CreateAndPrepare')
  }

  return (
    <V4CreateAndPrepare
      brandPlanId={context.brandPlanId}
      selectedStrategy={selectedStrategy}
      existingScheduleId={context.v4ScheduleId ?? null}
      onReady={(scheduleId) => {
        if (recommendationsReady) {
          send({ type: 'MERGE_CTX', payload: { v4ScheduleId: scheduleId } })
          send({ type: 'NEXT' })
        } else {
          pendingScheduleIdRef.current = scheduleId
        }
      }}
      onBack={() => send({ type: 'BACK' })}
    />
  )
}

const V4EditCampaignWrapper = ({ send, context }: StepProps) => {
  if (!context.v4ScheduleId) throw new Error('v4ScheduleId is required for V4EditCampaign')
  useDisplayedFontPairingsAndVisualStyles(context.brandKitId)
  return (
    <V4EditCampaign
      scheduleId={context.v4ScheduleId}
      onNext={() => send({ type: 'NEXT' })}
      onBack={() => send({ type: 'BACK' })}
    />
  )
}

const V4ChannelSelectionWrapper = ({ send, context }: StepProps) => {
  if (!context.v4ScheduleId) throw new Error('v4ScheduleId is required for V4ChannelSelection')
  if (!context.brandPlanId) throw new Error('brandPlanId is required for V4ChannelSelection')
  useDisplayedFontPairingsAndVisualStyles(context.brandKitId)
  return (
    <V4ChannelSelection
      scheduleId={context.v4ScheduleId}
      brandPlanId={context.brandPlanId}
      onNext={() => send({ type: 'NEXT' })}
      onBack={() => send({ type: 'BACK' })}
    />
  )
}

const V4ContentRecommendationsWrapper = ({ send, context }: StepProps) => {
  if (!context.v4ScheduleId) throw new Error('v4ScheduleId is required for V4ContentRecommendations')
  const { isLoading: isLoadingVisualStyleRecommendations, displayedVisualStyles } =
    useDisplayedFontPairingsAndVisualStyles(context.brandKitId)
  const { data: visualStyles } = useVisualStyles()
  const pendingNextRef = useRef(false)

  const recommendationsReady =
    !isLoadingVisualStyleRecommendations &&
    (displayedVisualStyles.length > 0 || (visualStyles != null && visualStyles.length > 0))

  useEffect(() => {
    if (pendingNextRef.current && recommendationsReady) {
      pendingNextRef.current = false
      send({ type: 'NEXT' })
    }
  }, [recommendationsReady, send])

  return (
    <V4ContentRecommendations
      scheduleId={context.v4ScheduleId}
      brandPlanId={context.brandPlanId}
      onNext={() => {
        prepareTopics({ scheduleId: context.v4ScheduleId }).catch(reportError)
        if (recommendationsReady) {
          send({ type: 'NEXT' })
        } else {
          pendingNextRef.current = true
        }
      }}
      onBack={() => send({ type: 'BACK' })}
    />
  )
}

const V4PrepareTopicsWrapper = ({ send, context }: StepProps) => {
  if (!context.v4ScheduleId) throw new Error('v4ScheduleId is required for V4PrepareTopics')
  useDisplayedFontPairingsAndVisualStyles(context.brandKitId)
  return (
    <V4PrepareTopics
      scheduleId={context.v4ScheduleId}
      brandKitId={context.brandKitId}
      onReady={() => send({ type: 'NEXT' })}
      onBack={() => send({ type: 'BACK' })}
    />
  )
}

const V4ReviewTopicsWrapper = ({ send, context }: StepProps) => {
  if (!context.v4ScheduleId) throw new Error('v4ScheduleId is required for V4ReviewTopics')
  useDisplayedFontPairingsAndVisualStyles(context.brandKitId)
  const workspaceId = useCurrentWorkspaceId()
  const { isMobile } = useResponsiveDesign()
  const { mutateAsync: onboard } = useOnboard()
  const { mutateAsync: markVMOnboardingCompleted } = useCompleteVMOnboarding()

  // Prefetch data needed by the next step (v4BrandMaterials) — desktop only
  useUploadsProject(workspaceId)
  useInfiniteBrandKitMediaFiles(
    { brandKitId: context.brandKitId ?? 0, items: 20, media_source: 'website', order_by: 'recent' },
    { enabled: !!context.brandKitId },
  )
  useInfiniteBrandKitMediaFiles(
    { brandKitId: context.brandKitId ?? 0, items: 50, media_source: 'brand_kit' },
    { enabled: !!context.brandKitId },
  )
  useBrandKitSources(
    { brandKitId: context.brandKitId ?? 0, sourceType: ['SourceMaterialsMediaObject'] },
    { enabled: !!context.brandKitId, refetchOnWindowFocus: false },
  )

  useEffect(() => {
    const scheduleId = context.v4ScheduleId
    const brandPlanId = context.brandPlanId
    if (context.v4RestCampaignsStarted || !scheduleId || !brandPlanId) return

    fetchBrandPlanSchedule({ id: scheduleId })
      .then((schedule) => {
        const restStartDate = dayjs(schedule.start_date).add(schedule.length, 'week').format('YYYY-MM-DD')
        return startBrandPlan({ brandPlanId, workspaceId, start_date: restStartDate })
      })
      .then(() => {
        send({ type: 'MERGE_CTX', payload: { v4RestCampaignsStarted: true } })
      })
      .catch((err) => {
        reportError(err)
        showToastMessage(
          'Something went wrong starting your content plan. Please contact support if this persists.',
          'error',
        )
      })
  }, [context.v4ScheduleId, context.brandPlanId, context.v4RestCampaignsStarted, workspaceId, send])

  return (
    <V4ReviewTopics
      scheduleId={context.v4ScheduleId}
      onNext={async () => {
        if (context.v4ScheduleId) {
          generateCampaignContent({ campaignId: context.v4ScheduleId }).catch(reportError)
        }

        await onboard()
        await markVMOnboardingCompleted()

        send({ type: isMobile ? 'NEXT_MOBILE' : 'NEXT' })
      }}
      onBack={() => send({ type: 'BACK' })}
    />
  )
}

const V4VisualStyleSelectionWrapper = ({ send, context }: StepProps) => {
  return (
    <V4VisualStyleSelection
      brandKitId={context.brandKitId}
      initialSelectedStyleId={context.selectedVisualStyle?.id}
      businessType={context.selectedBusinessType}
      onBack={() => send({ type: 'BACK' })}
      onNext={(selectedVisualStyle, fontPairings) => {
        send({
          type: 'MERGE_CTX',
          payload: {
            selectedVisualStyle,
            stylesGenerated: [selectedVisualStyle],
            ...(fontPairings && { fontPairings }),
          },
        })
        send({ type: 'NEXT' })
      }}
    />
  )
}

const V4ContentPreferencesWrapper = ({ send, context }: StepProps) => {
  if (!context.brandKitId || !context.brandPlanId) {
    throw new Error('Brand kit ID and brand plan ID are required for V4ContentPreferences')
  }

  return (
    <V4ContentPreferences
      brandKitId={context.brandKitId}
      initialVariant={context.sourceImageEditLevel}
      onBack={() => send({ type: 'BACK' })}
      onNext={(sourceImageEditLevel) => {
        send({ type: 'MERGE_CTX', payload: { sourceImageEditLevel } })
        send({ type: 'NEXT' })
      }}
    />
  )
}

const V4LearningLoopWrapper = ({ send, context }: StepProps) => {
  if (!context.brandPlanId) throw new Error('brandPlanId is required for V4LearningLoop')
  return (
    <ContentOptimization
      brandPlanId={context.brandPlanId}
      brandPlanStrategies={context.brandPlanStrategies}
      highlightStep="finishingUp"
      onNext={() => send({ type: 'NEXT' })}
      onBack={() => send({ type: 'BACK' })}
    />
  )
}

const V4CampaignPreviewWrapper = ({ send, context }: StepProps) => {
  if (!context.brandPlanId || !context.v4ScheduleId)
    throw new Error('brandPlanId and v4ScheduleId are required for V4CampaignPreview')
  return (
    <V4CampaignPreview
      brandPlanId={context.brandPlanId}
      v4ScheduleId={context.v4ScheduleId}
      onNext={() => send({ type: 'NEXT' })}
      onBack={() => send({ type: 'BACK' })}
    />
  )
}

const V4BrandMaterialsWrapper = ({ send, context }: StepProps) => {
  if (!context.brandKitId) throw new Error('brandKitId is required for V4BrandMaterials')

  // Prefetch data needed by the next step (v4CampaignPreview)
  useBrandPlanSchedules(
    { brandPlanId: context.brandPlanId ?? 0, order_direction: 'asc', items: 4 },
    { enabled: !!context.brandPlanId },
  )

  return (
    <V4BrandMaterials
      brandKitId={context.brandKitId}
      onBack={() => send({ type: 'BACK' })}
      onNext={() => send({ type: 'NEXT' })}
      hideBack
    />
  )
}

const V4BrandVisualStyleFontsWrapper = ({ send, context, onProvideBackHandler }: StepProps) => {
  return (
    <BrandVisualStyleFonts
      onBack={() => send({ type: 'BACK' })}
      onProvideBackHandler={onProvideBackHandler}
      brandKitId={context.brandKitId}
      initialFontPairings={context.fontPairings}
      initialFontPairingId={context.selectedFontPairingId}
      selectedStyle={context.selectedVisualStyle}
      highlightStep="content"
      onNext={(selectedFontPairingId) => {
        send({
          type: 'MERGE_CTX',
          payload: { selectedFontPairingId },
        })
        send({ type: 'NEXT' })
      }}
    />
  )
}

const flowStep = step<FlowCtx>

const steps = [
  flowStep('website', BrandProfileWebsiteWrapper)
    .withExtra((ctx) => ({
      createBrandProfileAnalysis: ctx.createBrandProfileAnalysis,
    }))
    .on('NO_WEBSITE', 'noWebsite')
    .on('NEXT', 'profile'),

  flowStep('screenshotFallback', ScreenshotFallbackWrapper)
    .skipWhen((ctx) => ctx.websiteScrapeSucceeded)
    .back('website'),
  flowStep('noWebsite', NoWebsiteWrapper).back('website'),
  flowStep('profile', BrandProfileStepWrapper)
    .withSiderComponent(ProfileAutopilotSidebar)
    .back('website', (ctx) => ctx.websiteScrapeSucceeded)
    .back('screenshotFallback', (ctx) => !ctx.websiteScrapeSucceeded)
    .on('NEXT', 'brandVisualStyleSelection')
    .on('NEXT_MOBILE', 'brandVisualStylePreview'),

  flowStep('brandVisualStyleSelection', BrandVisualStyleSelectionWrapper)
    .back('profile')
    .on('NEXT', 'brandVisualStylePreview'),

  flowStep('brandVisualStylePreview', BrandVisualStylePreviewWrapper)
    .back('brandVisualStyleSelection')
    .on('BACK_MOBILE', 'profile')
    .on('NEXT', 'brandVisualStyleFonts'),

  flowStep('brandVisualStyleFonts', BrandVisualStyleFontsWrapper)
    .back('brandVisualStylePreview')
    .on('NEXT', 'brandVisualStyleVoiceAndLogo'),

  flowStep('brandVisualStyleVoiceAndLogo', BrandVisualStyleVoiceAndLogoWrapper)
    .back('brandVisualStyleFonts')
    .on('NEXT', 'whereToPublish'),

  flowStep('whereToPublish', WhereToPublishWrapper)
    .back('brandVisualStyleVoiceAndLogo')
    .on('BACK_TO_VISUAL_STYLES_VOICE', 'brandVisualStyleVoiceAndLogo')
    .on('BACK_TO_FONTS', 'brandVisualStyleFonts')
    .on('NEXT', 'awareness')
    .on('NEXT_STARTER', 'awareness')
    .on('NEXT_GROWTH', 'campaignDetails'),

  flowStep('campaignDetails', CampaignDetailsWrapper).back('whereToPublish').on('NEXT', 'campaignGenerationLoading'),

  flowStep('awareness', BrandPlanAwarenessWrapper)
    .back('whereToPublish')
    .on('NEXT', 'done')
    .on('MOBILE_COMPLETE', 'done'),

  flowStep('campaignGenerationLoading', CampaignGenerationLoadingWrapper)
    .isFullScreen()
    .on('BACK', 'campaignDetails')
    .on('COMPLETE', 'done'),
]

const stepsWithStrategies = [
  flowStep('website', BrandProfileWebsiteWrapper)
    .withExtra((ctx) => ({
      createBrandProfileAnalysis: ctx.createBrandProfileAnalysis,
    }))
    .on('NEXT', 'profile'),

  flowStep('profile', BrandProfileForStrategiesStepWrapper)
    .withSiderComponent(ProfileAutopilotSidebar)
    .back('website', (ctx) => ctx.websiteScrapeSucceeded)
    .on('NEXT', 'goalSelection'),

  flowStep('goalSelection', GoalSelectionWrapper).back('profile').on('NEXT', 'strategiesSelection'),
  flowStep('strategiesSelection', StrategiesSelectionWrapper)
    .back('goalSelection')
    .on('NEXT', 'brandVisualStylePreview'),

  flowStep('brandVisualStylePreview', BrandVisualStylePreviewWrapper)
    .back('strategiesSelection')
    .on('NEXT', 'brandVisualStyleFonts'),

  flowStep('brandVisualStyleFonts', BrandVisualStyleFontsWrapper)
    .back('brandVisualStylePreview')
    .on('NEXT', 'nextGenContentPreferences'),

  flowStep('nextGenContentPreferences', NextGenContentPreferencesWrapper)
    .back('brandVisualStyleFonts')
    .on('NEXT_MOBILE', 'mobileGenerationAndQuestionnaire')
    .on('NEXT', 'campaignGenerationLoading'),

  flowStep('campaignGenerationLoading', CampaignGenerationLoadingWrapper)
    .isFullScreen()
    .on('BACK', 'nextGenContentPreferences')
    .on('COMPLETE', 'done'),

  flowStep('mobileGenerationAndQuestionnaire', MobileGenerationAndQuestionnaireWrapper).hideBackButton(),
]

const stepsV4 = [
  flowStep('marketingQuestions', MarketingQuestionsWrapper)
    .isFullScreen()
    .hideBackButton()
    .skipWhen((ctx) => !!ctx.hasMarketingData)
    .on('NEXT', 'website'),

  flowStep('website', V4WebsiteEntryWrapper)
    .isFullScreen()
    .withExtra((ctx) => ({
      createBrandProfileAnalysis: ctx.createBrandProfileAnalysis,
    }))
    .on('NEXT', 'v4ProfileLoader'),

  flowStep('v4ProfileLoader', V4ProfileLoaderWrapper).isFullScreen().back('website').on('NEXT', 'profile'),

  flowStep('profile', BrandProfileForStrategiesStepWrapper)
    .withSiderComponent(ProfileAutopilotSidebar)
    .back('website')
    .on('NEXT', 'strategiesSelection'),

  flowStep('strategiesSelection', V4StrategiesSelectionWrapper)
    .isFullWidth()
    .back('profile')
    .on('NEXT_V4', 'v4CreateAndPrepare'),

  flowStep('v4CreateAndPrepare', V4CreateAndPrepareWrapper)
    .isFullScreen()
    .on('BACK', 'strategiesSelection')
    .on('NEXT', 'v4EditCampaign'),

  flowStep('v4EditCampaign', V4EditCampaignWrapper)
    .isFullWidth()
    .back('strategiesSelection')
    .on('NEXT', 'v4ChannelSelection'),

  flowStep('v4ChannelSelection', V4ChannelSelectionWrapper)
    .isFullWidth()
    .back('v4EditCampaign')
    .on('NEXT', 'v4ContentRecommendations'),

  flowStep('v4ContentRecommendations', V4ContentRecommendationsWrapper)
    .isFullWidth()
    .back('v4ChannelSelection')
    .on('NEXT', 'v4VisualStyleSelection'),

  flowStep('v4VisualStyleSelection', V4VisualStyleSelectionWrapper)
    .isFullScreen()
    .back('v4ContentRecommendations')
    .on('NEXT', 'brandVisualStyleFonts'),

  flowStep('brandVisualStyleFonts', V4BrandVisualStyleFontsWrapper)
    .back('v4VisualStyleSelection')
    .on('NEXT', 'v4ContentPreferences'),

  flowStep('v4ContentPreferences', V4ContentPreferencesWrapper)
    .isFullWidth()
    .back('brandVisualStyleFonts')
    .on('NEXT', 'v4PrepareTopics'),

  flowStep('v4PrepareTopics', V4PrepareTopicsWrapper)
    .isFullScreen()
    .on('BACK', 'v4ContentRecommendations')
    .on('NEXT', 'v4ReviewTopics'),

  flowStep('v4ReviewTopics', V4ReviewTopicsWrapper)
    .isFullWidth()
    .back('v4ContentRecommendations')
    .on('NEXT', 'v4BrandMaterials')
    .on('NEXT_MOBILE', 'mobileGenerationAndQuestionnaire'),

  flowStep('v4BrandMaterials', V4BrandMaterialsWrapper).isFullWidth().hideBackButton().on('NEXT', 'v4CampaignPreview'),

  flowStep('v4CampaignPreview', V4CampaignPreviewWrapper).isFullWidth().back('v4BrandMaterials').on('NEXT', 'done'),

  flowStep('mobileGenerationAndQuestionnaire', MobileGenerationAndQuestionnaireWrapper).hideBackButton(),
]

const V4SlimChannelSelectionWrapper = ({ send, context }: StepProps) => {
  if (!context.brandPlanId) throw new Error('brandPlanId is required for V4SlimChannelSelection')
  return (
    <V4ChannelSelection
      brandPlanId={context.brandPlanId}
      onNext={() => send({ type: 'NEXT' })}
      onBack={() => send({ type: 'BACK' })}
    />
  )
}

const V4SlimContentRecommendationsWrapper = ({ send, context }: StepProps) => {
  if (!context.brandPlanId) throw new Error('brandPlanId is required for V4SlimContentRecommendations')
  const { isLoading: isLoadingVisualStyleRecommendations } = useDisplayedFontPairingsAndVisualStyles(context.brandKitId)
  const pendingNextRef = useRef(false)

  useEffect(() => {
    if (pendingNextRef.current && !isLoadingVisualStyleRecommendations) {
      pendingNextRef.current = false
      send({ type: 'NEXT' })
    }
  }, [isLoadingVisualStyleRecommendations, send])

  return (
    <V4ContentRecommendations
      brandPlanId={context.brandPlanId}
      onNext={() => {
        if (isLoadingVisualStyleRecommendations) {
          pendingNextRef.current = true
        } else {
          send({ type: 'NEXT' })
        }
      }}
      onBack={() => send({ type: 'BACK' })}
    />
  )
}

const stepsV4Slim = [
  flowStep('website', V4WebsiteEntryWrapper)
    .isFullScreen()
    .withExtra((ctx) => ({
      createBrandProfileAnalysis: ctx.createBrandProfileAnalysis,
    }))
    .on('NEXT', 'v4ProfileLoader'),

  flowStep('v4ProfileLoader', V4ProfileLoaderWrapper).isFullScreen().back('website').on('NEXT', 'profile'),

  flowStep('profile', BrandProfileForStrategiesStepWrapper)
    .withSiderComponent(ProfileAutopilotSidebar)
    .back('website')
    .on('NEXT', 'strategiesSelection'),

  flowStep('strategiesSelection', V4StrategiesSelectionWrapper)
    .isFullWidth()
    .back('profile')
    .on('NEXT_V4', 'v4SlimChannelSelection'),

  flowStep('v4SlimChannelSelection', V4SlimChannelSelectionWrapper)
    .isFullWidth()
    .back('strategiesSelection')
    .on('NEXT', 'v4SlimContentRecommendations'),

  flowStep('v4SlimContentRecommendations', V4SlimContentRecommendationsWrapper)
    .isFullWidth()
    .back('v4SlimChannelSelection')
    .on('NEXT', 'v4VisualStyleSelection'),

  flowStep('v4VisualStyleSelection', V4VisualStyleSelectionWrapper)
    .isFullScreen()
    .back('v4SlimContentRecommendations')
    .on('NEXT', 'brandVisualStyleFonts'),

  flowStep('brandVisualStyleFonts', V4BrandVisualStyleFontsWrapper)
    .back('v4VisualStyleSelection')
    .on('NEXT', 'v4ContentPreferences'),

  flowStep('v4ContentPreferences', V4ContentPreferencesWrapper)
    .isFullWidth()
    .back('brandVisualStyleFonts')
    .on('NEXT', 'v4LearningLoop'),

  flowStep('v4LearningLoop', V4LearningLoopWrapper)
    .back('v4ContentPreferences')
    .on('NEXT', 'campaignGenerationLoading'),

  flowStep('campaignGenerationLoading', CampaignGenerationLoadingWrapper)
    .isFullScreen()
    .on('BACK', 'v4ContentPreferences')
    .on('COMPLETE', 'done'),
]

const resolveSteps = (flow: OnboardingFlow) => {
  if (flow === 'v4slim') return stepsV4Slim
  if (flow === 'v4') return stepsV4
  if (flow === 'strategies') return stepsWithStrategies
  return steps
}

export const useVirtualMarketerRedesignSteps = () => {
  const flow = useOnboardingFlow()

  return useMemo(() => defineSteps<FlowCtx>(resolveSteps(flow)), [flow])
}
